import { RawDraftContentState } from 'draft-js';
import randomString from '../../../../../uid/randomString';

/**
 * Converts Draft.js caption content into content suitable for Tiptap conversion.
 *
 * This is because Draft.js didn't support paragraph styling within captions, thus we need to
 * join all blocks together using soft line breaks, so they will render the same in Tiptap.
 *
 * The logic below ensures that the entity and inline style ranges are adjusted correctly.
 */
export const prepareDraftJsCaptionForConversion = (captionContent: RawDraftContentState): RawDraftContentState => {
    let currentOffset = 0;
    const entityRanges = [];
    const inlineStyleRanges = [];

    for (const block of captionContent.blocks) {
        const blockText = block.text || '';
        const blockTextLength = blockText.length;

        const blockEntityRanges = block.entityRanges || [];
        const blockInlineStyleRanges = block.inlineStyleRanges || [];

        const newBlockEntityRanges = blockEntityRanges.map((entityRange) => {
            const newEntityRange = { ...entityRange };
            newEntityRange.offset = entityRange.offset + currentOffset;
            return newEntityRange;
        });

        entityRanges.push(...newBlockEntityRanges);

        const newBlockInlineStyleRanges = blockInlineStyleRanges.map((inlineStyleRange) => {
            const newInlineStyleRange = { ...inlineStyleRange };
            newInlineStyleRange.offset = inlineStyleRange.offset + currentOffset;
            return newInlineStyleRange;
        });

        inlineStyleRanges.push(...newBlockInlineStyleRanges);

        // We need to add 1 for the newline character
        currentOffset += blockTextLength + 1;
    }

    const newCaptionContent: RawDraftContentState = { ...captionContent };

    const captionText = captionContent.blocks?.map((block) => block.text).join('\n') || '';

    const firstBlock = captionContent.blocks?.[0] || {};
    const firstBlockKey = firstBlock.key || randomString(6);
    const firstBlockData = firstBlock.data;

    // Simplify the caption content to a single block
    newCaptionContent.blocks = [
        {
            key: firstBlockKey,
            text: captionText,
            type: 'unstyled',
            depth: 0,
            entityRanges,
            inlineStyleRanges,
            data: firstBlockData,
        },
    ];

    return newCaptionContent;
};
